import React, { useState, useRef, useEffect } from 'react';
import { Network, Building2, Users, ChevronDown, FileSpreadsheet, FileText, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';

type ViewMode = 'hierarchy' | 'department';

interface OrgChartHeaderProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  totalMembers: number;
  onExportPdf: () => void;
  onExportExcel: () => void;
}

export function OrgChartHeader({
  viewMode,
  onViewModeChange,
  totalMembers,
  onExportPdf,
  onExportExcel,
}: OrgChartHeaderProps) {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#6825F5]/10 text-[#6825F5] shadow-sm ring-1 ring-[#6825F5]/20">
          <Network className="h-6 w-6" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-display-sm font-bold text-[#11183F] tracking-tight">Organization Chart</h1>
            <span className="rounded-full bg-[#6825F5]/10 px-2.5 py-0.5 text-xs font-semibold text-[#6825F5]">
              {totalMembers} Team Members
            </span>
          </div>
          <p className="text-body-sm text-[#747C9C]">
            Hierarchy structured by Executive Leadership, Department Administrators, and Functional Roles
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        {/* View Mode Switcher */}
        <div className="flex items-center rounded-lg border border-[#E8E8F5] bg-[#FFFFFF] p-0.5 shadow-sm">
          <button
            type="button"
            onClick={() => onViewModeChange('hierarchy')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
              viewMode === 'hierarchy'
                ? 'bg-[#6825F5] text-white shadow-sm'
                : 'text-[#747C9C] hover:text-[#11183F] hover:bg-[#F2F2FA]'
            }`}
            title="Organized by Department and Admin Hierarchy"
          >
            <Network className="h-3.5 w-3.5" />
            <span>Hierarchy Tree</span>
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange('department')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
              viewMode === 'department'
                ? 'bg-[#6825F5] text-white shadow-sm'
                : 'text-[#747C9C] hover:text-[#11183F] hover:bg-[#F2F2FA]'
            }`}
            title="Matrix view grouped by Departments"
          >
            <Building2 className="h-3.5 w-3.5" />
            <span>By Department</span>
          </button>
        </div>

        {/* Export Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="gap-1.5 px-3 py-1.5 text-xs font-medium border-[#E8E8F5] text-[#747C9C] hover:bg-[#F2F2FA]"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            <span>Export</span>
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>

          {showExportMenu && (
            <div className="absolute right-0 top-full mt-1.5 z-50 min-w-[180px] rounded-lg border border-[#E8E8F5] bg-white shadow-lg overflow-hidden animate-in">
              <div className="py-1">
                <button
                  type="button"
                  onClick={() => {
                    onExportPdf();
                    setShowExportMenu(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[#11183F] hover:bg-[#F2F2FA] transition-colors"
                >
                  <FileText className="h-4 w-4 text-[#E11D48]" />
                  <span>Export as PDF</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onExportExcel();
                    setShowExportMenu(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[#11183F] hover:bg-[#F2F2FA] transition-colors"
                >
                  <FileSpreadsheet className="h-4 w-4 text-[#20B982]" />
                  <span>Export as Excel</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}